import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Text,
  Textarea,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
  useColorModeValue,
  useToast,
  Badge,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { format, subDays } from "date-fns";
import { FiMic, FiVolumeX } from "react-icons/fi";
import {
  FaBriefcase,
  FaCalendarAlt,
  FaCheckSquare,
  FaClipboardList,
  FaFileAlt,
  FaLink,
  FaTasks,
  FaUser,
  FaUserFriends,
  FaHeadphones,
  FaPhotoVideo,
  FaMicrophone,
  FaVideo,
  FaExchangeAlt,
  FaGraduationCap,
  FaSun,
  FaSitemap,
} from "react-icons/fa";
import { hitBeApi } from "../../api/api";
import TabCacheButton from "../common/TabCacheButton";
import { TiltCard } from "../common/TiltCard";
import {
  getSpeechRecognitionClass,
  getPreferredSttLang,
  startBrowserVoiceDictation,
} from "../../utils/browserSpeechRecognition";
import { speakBrowserAssistantReply } from "../../utils/browserSpeechSpeak";
import { SITE_STRIP_LINE, DASHBOARD_HOME_TOOL_BLURBS } from "../../content/productGuide";

function parseYmd(d) {
  const [y, m, day] = String(d).split("-").map(Number);
  return new Date(y, m - 1, day);
}

/** IST calendar date string (matches Daily chore page / server chore day). */
function choreViewDateIST() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

const VOICE_DOMAINS = [
  {
    id: "portfolio",
    label: "Portfolio AI",
    to: "portfolio",
    hint: "Money questions and quick entries—matches the Portfolio AI tab.",
  },
  {
    id: "projects",
    label: "Project AI",
    to: "project",
    hint: "Tasks and columns—matches the Project AI tab.",
  },
  {
    id: "daily-chore",
    label: "Daily Chore AI",
    to: "daily-chore",
    hint: "Today’s chore list (India date on the server)—add, finish, or ask what’s left.",
  },
  {
    id: "todos",
    label: "Todos AI",
    to: "todos",
    hint: "Your todo tree for today—add, rename, complete, or tidy sub-tasks.",
  },
];

const AI_STUDIO = [
  {
    to: "screen-listen",
    title: "Screen AI",
    icon: FaHeadphones,
    accentColor: "cyan",
    desc: "Share a tab—screenshots and audio become saved, searchable summaries.",
  },
  {
    to: "media-analyze",
    title: "Media AI",
    icon: FaPhotoVideo,
    accentColor: "purple",
    desc: "Upload images or video; AI explains each file or time segment.",
  },
  {
    to: "voice-analyse",
    title: "Audio AI",
    icon: FaMicrophone,
    accentColor: "teal",
    desc: "Record or upload voice notes—multiple parts in one box—then get transcripts and analysis.",
  },
  {
    to: "video-ai",
    title: "Video AI",
    icon: FaVideo,
    accentColor: "purple",
    desc: "A timestamped timeline of your videos—ask who or what appears, and when.",
  },
  {
    to: "transfer-ai",
    title: "Transfer AI",
    icon: FaExchangeAlt,
    accentColor: "orange",
    desc: "Post your travel route; others find you by city and date and chat to request a favour.",
  },
  {
    to: "exam-ai",
    title: "Exam AI",
    icon: FaGraduationCap,
    accentColor: "pink",
    desc: "Auto-built syllabus tree with a fact-checked tutor and current-affairs MCQs.",
  },
  {
    to: "day-life",
    title: "DayLife AI",
    icon: FaSun,
    accentColor: "yellow",
    desc: "Daily journal with an AI coach that spots patterns over time.",
  },
];

const OTHER_FEATURES = [
  {
    to: "notes",
    title: "Notes",
    icon: FaLink,
    desc: "Folders, links, and notes with search.",
    voice: false,
  },
  {
    to: "org-tree",
    title: "Org tree",
    icon: FaSitemap,
    desc: "3D org map with todos per node and free voice navigation.",
    voice: false,
  },
  {
    to: "files",
    title: "Files",
    icon: FaFileAlt,
    desc: "Upload and open files from folders.",
    voice: false,
  },
  {
    to: "calendar-events",
    title: "Calendar",
    icon: FaCalendarAlt,
    desc: "Reminders tied to your calendar flow.",
    voice: false,
  },
  {
    to: "splitwise",
    title: "Groups",
    icon: FaUserFriends,
    desc: "Split costs and see group balances.",
    voice: false,
  },
  {
    to: "profile",
    title: "Profile",
    icon: FaUser,
    desc: "Your account and phone or email for alerts.",
    voice: false,
  },
];

function FeatureCard({
  to,
  title,
  icon: IconComp,
  desc,
  voice,
  aiBadge,
  cardBg,
  border,
  accent,
  accentColor,
}) {
  const scheme = accentColor || "teal";
  return (
    <TiltCard
      as={RouterLink}
      to={to}
      tilt={7}
      display="block"
      bg={cardBg}
      p={5}
      borderRadius="xl"
      borderWidth={aiBadge ? "2px" : "1px"}
      borderColor={aiBadge ? `${scheme}.300` : border}
      boxShadow="sm"
      h="full"
      transition="box-shadow 0.25s ease, border-color 0.25s ease"
      _hover={{
        boxShadow: `0 18px 48px -24px var(--chakra-colors-${scheme}-400)`,
      }}
      sx={{ transformStyle: "preserve-3d" }}
    >
      <HStack align="start" spacing={3}>
        <Box
          p={2.5}
          borderRadius="lg"
          bg={accentColor ? `${scheme}.50` : accent.soft}
          color={accentColor ? `${scheme}.500` : accent.solid}
          flexShrink={0}
          sx={{
            _dark: {
              bg: "whiteAlpha.100",
              color: accentColor ? `${scheme}.300` : undefined,
            },
            transform: "translateZ(22px)",
            boxShadow: "0 8px 20px -8px rgba(0,0,0,0.25)",
          }}
        >
          <Icon as={IconComp} boxSize={5} />
        </Box>
        <Box sx={{ transform: "translateZ(12px)" }}>
          <HStack spacing={2} mb={1}>
            <Text fontWeight="bold" fontSize="md">
              {title}
            </Text>
            {aiBadge ? (
              <Badge colorScheme={scheme} fontSize="0.65rem">
                AI
              </Badge>
            ) : null}
            {voice ? (
              <Badge colorScheme="teal" fontSize="0.65rem">
                Voice
              </Badge>
            ) : null}
          </HStack>
          <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
            {desc}
          </Text>
          <ChakraLink
            as="span"
            fontSize="xs"
            color="teal.600"
            fontWeight="semibold"
            mt={2}
            display="inline-block"
            _dark={{ color: "teal.300" }}
          >
            Open →
          </ChakraLink>
        </Box>
      </HStack>
    </TiltCard>
  );
}

const DashboardHome = () => {
  const toast = useToast();
  const [voiceDraft, setVoiceDraft] = useState("");
  const [voiceReply, setVoiceReply] = useState("");
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceSpeaking, setVoiceSpeaking] = useState(false);
  const portfolioPendingRef = useRef(null);
  const submitVoiceRef = useRef(async () => {});

  const pageBg = useColorModeValue("gray.50", "gray.900");
  const heroBg = useColorModeValue(
    "linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 45%, #ede9fe 100%)",
    "linear-gradient(135deg, rgba(15,118,110,0.25) 0%, rgba(30,64,175,0.2) 50%, rgba(76,29,149,0.22) 100%)"
  );
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.600");
  const muted = useColorModeValue("gray.600", "gray.400");
  const accent = {
    soft: useColorModeValue("teal.50", "teal.900"),
    solid: useColorModeValue("teal.600", "teal.200"),
  };
  const voicePanelInnerBg = useColorModeValue(
    "linear-gradient(180deg, white 0%, #f8fafc 100%)",
    "linear-gradient(180deg, var(--chakra-colors-gray-800) 0%, var(--chakra-colors-gray-900) 100%)"
  );
  const voiceComposerBg = useColorModeValue("gray.50", "gray.900");

  const stopSpeech = useCallback(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setVoiceSpeaking(false);
  }, []);

  const speakReply = useCallback((text) => {
    if (typeof window === "undefined" || !text) return;
    try {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      speakBrowserAssistantReply(text, {
        lang: "en-IN",
        flushDelayMs: 0,
        onBeforeSpeak: () => setVoiceSpeaking(true),
        onEnd: () => setVoiceSpeaking(false),
        onError: () => setVoiceSpeaking(false),
      });
    } catch {
      setVoiceSpeaking(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const submitVoice = useCallback(
    async (overrideText) => {
      const text = String(
        overrideText !== undefined && overrideText !== null ? overrideText : voiceDraft
      ).trim();
      if (!text) {
        toast({
          title: "Nothing to send",
          description: "Type or tap the mic and speak.",
          status: "warning",
          duration: 2500,
          isClosable: true,
        });
        return;
      }
      stopSpeech();
      setVoiceLoading(true);
      try {
        const viewDay = format(new Date(), "yyyy-MM-dd");
        const prevDay = format(subDays(parseYmd(viewDay), 1), "yyyy-MM-dd");
        const res = await hitBeApi("dashboard/voice-assistant", "POST", {
          text,
          pendingTransaction: portfolioPendingRef.current,
          viewDay,
          prevDay,
          choreViewDate: choreViewDateIST(),
        });
        if (res?.success && res?.data) {
          const d = res.data;
          const reply = d.reply || "";
          const routed = d.routedDomain || null;
          const hasActions = Array.isArray(d.actions) && d.actions.length > 0;
          if (routed === "portfolio") {
            portfolioPendingRef.current = d.pendingTransaction ?? null;
            if (hasActions) {
              portfolioPendingRef.current = null;
            }
          } else {
            portfolioPendingRef.current = null;
          }
          setVoiceReply(reply);

          const routeLabel =
            routed === "portfolio"
              ? "Portfolio AI"
              : routed === "projects"
                ? "Project AI"
                : routed === "daily_chore"
                  ? "Daily Chore AI"
                  : routed === "todos"
                    ? "Todos AI"
                    : null;
          const toastTitle =
            routeLabel && hasActions
              ? `${routeLabel} updated`
              : routeLabel
                ? `${routeLabel} assistant`
                : "Assistant";

          toast({
            title: toastTitle,
            description: reply || "Done.",
            status: hasActions ? "success" : "info",
            duration: 4500,
            isClosable: true,
          });
          speakReply(reply);
        }
      } catch (e) {
        const msg = e?.message || "Try again.";
        const isCreditsIssue =
          msg.toLowerCase().includes("credit") ||
          msg.toLowerCase().includes("token");
        toast({
          title: isCreditsIssue ? "AI Usage Credits required" : "Assistant error",
          description: msg,
          status: isCreditsIssue ? "warning" : "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setVoiceLoading(false);
      }
    },
    [voiceDraft, toast, stopSpeech, speakReply]
  );

  submitVoiceRef.current = submitVoice;

  const runMicOnce = () => {
    stopSpeech();
    startBrowserVoiceDictation({
      lang: getPreferredSttLang(),
      onListeningStart: () => setVoiceListening(true),
      onInterim: (t) => setVoiceDraft(t),
      onListeningEnd: () => setVoiceListening(false),
      onFinal: (t) => {
        setVoiceDraft(t);
        submitVoiceRef.current(t);
      },
      onError: () => {},
    });
  };

  const onMic = () => {
    if (!getSpeechRecognitionClass()) {
      toast({
        title: "Voice not supported",
        description: "Use Chrome or Edge, or type your command.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (voiceLoading || voiceListening) return;
    runMicOnce();
  };

  return (
    <Box bg={pageBg} minH="100%" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
      <VStack align="stretch" spacing={8} maxW="6xl" mx="auto">
        <Box
          bg={heroBg}
          borderRadius="2xl"
          px={{ base: 5, md: 10 }}
          py={{ base: 8, md: 10 }}
          borderWidth="1px"
          borderColor={border}
          boxShadow="md"
        >
          <Flex
            align="flex-start"
            justify="space-between"
            gap={4}
            flexDir={{ base: "column", sm: "row" }}
            mb={0}
          >
            <Box>
              <Badge
                colorScheme="teal"
                variant="subtle"
                mb={3}
                fontSize="0.65rem"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Manage360.me
              </Badge>
              <Heading as="h1" size="xl" fontWeight="extrabold" letterSpacing="tight" mb={3}>
                Your workspace hub
              </Heading>
              <Text fontSize="md" color={muted} maxW="3xl" lineHeight="tall">
                {SITE_STRIP_LINE}
              </Text>
              <Text fontSize="sm" color={muted} maxW="3xl" lineHeight="tall" mt={3}>
                The mic opens voice help on{" "}
                <strong>Portfolio AI</strong>, <strong>Project AI</strong>,{" "}
                <strong>Daily Chore AI</strong>, and <strong>Todos AI</strong>. Use the sidebar for
                Screen AI, Media AI, Audio AI, Video AI, Transfer AI, Exam AI, notes, calendar, and
                more.
              </Text>
              <Box
                mt={4}
                bg={useColorModeValue("white", "gray.800")}
                borderWidth="1px"
                borderColor={border}
                borderRadius="xl"
                px={4}
                py={3}
                boxShadow="sm"
              >
                <HStack spacing={3} align="start">
                  <Box
                    bg={accent.soft}
                    color={accent.solid}
                    borderRadius="full"
                    p={2}
                    mt={0.5}
                  >
                    <Icon as={FiMic} boxSize={4} />
                  </Box>
                  <Box>
                    <Badge colorScheme="teal" mb={1}>
                      AI Usage Credits
                    </Badge>
                    <Text fontSize="sm" color={muted} lineHeight="tall">
                      AI Usage Credits are used when you talk to the assistant on Portfolio AI,
                      Project AI, Todos AI, Daily Chore AI, Screen AI, and Media AI.
                    </Text>
                  </Box>
                </HStack>
              </Box>
            </Box>
            <TabCacheButton tabId="home" />
          </Flex>
          <Box mt={6}>
            <Text fontSize="sm" fontWeight="bold" color={muted} mb={2} letterSpacing="wide">
              INCLUDED TOOLS — TAP TO OPEN
            </Text>
            <Wrap spacing={2} justify="flex-start">
              {DASHBOARD_HOME_TOOL_BLURBS.map((tool) => (
                <WrapItem key={tool.tab}>
                  <ChakraLink
                    as={RouterLink}
                    to={tool.tab}
                    display="inline-block"
                    px={3}
                    py={1.5}
                    borderRadius="lg"
                    fontSize="sm"
                    bg={accent.soft}
                    color={accent.solid}
                    fontWeight="semibold"
                    borderWidth="1px"
                    borderColor={border}
                    _hover={{ opacity: 0.92, transform: "translateY(-1px)" }}
                    title={tool.line}
                  >
                    {tool.title}
                  </ChakraLink>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        </Box>

        <Box
          bg={cardBg}
          borderRadius="2xl"
          borderWidth="1px"
          borderColor={border}
          overflow="hidden"
          boxShadow="lg"
        >
          <Box px={{ base: 4, md: 8 }} py={{ base: 6, md: 8 }} bg={voicePanelInnerBg}>
            <Text
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
              letterSpacing="0.14em"
              color={muted}
              mb={2}
            >
              Voice assistant
            </Text>
            <Heading size="md" mb={3}>
              Voice hub
            </Heading>

            <VStack spacing={6} pt={1} align="stretch">
              <Flex
                direction={{ base: "column", lg: "row" }}
                gap={{ base: 6, lg: 10 }}
                align={{ base: "center", lg: "flex-start" }}
              >
                <VStack spacing={3} align="center" flexShrink={0} minW="0">
                  <Tooltip label="Browser speech (Chrome / Edge). The server routes your request to Portfolio AI, Project AI, Daily Chore AI, or Todos AI.">
                    <IconButton
                      aria-label="Start voice"
                      icon={<Icon as={FiMic} boxSize={{ base: 12, md: 14 }} />}
                      w={{ base: "120px", md: "140px" }}
                      h={{ base: "120px", md: "140px" }}
                      minW={{ base: "120px", md: "140px" }}
                      minH={{ base: "120px", md: "140px" }}
                      borderRadius="full"
                      colorScheme="teal"
                      variant="solid"
                      fontSize="4xl"
                      boxShadow="0 12px 40px -8px rgba(20,184,166,0.55)"
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "0 18px 44px -10px rgba(20,184,166,0.6)",
                      }}
                      _active={{ transform: "translateY(0)" }}
                      transition="all 0.2s ease"
                      onClick={onMic}
                      isLoading={voiceListening}
                      isDisabled={voiceLoading}
                    />
                  </Tooltip>
                  <Text fontSize="sm" color={voiceListening ? "teal.500" : muted} fontWeight="medium">
                    {voiceListening ? "Listening… speak now" : "Tap the mic to speak"}
                  </Text>
                </VStack>

                <VStack spacing={4} align="stretch" flex="1" minW="0" textAlign="left">
                  <Text fontSize="sm" color={muted} lineHeight="tall">
                    Talk about money, tasks, chores, or todos—we route it for you. Todos follow{" "}
                    <strong>your device’s date</strong> for “today”; daily chores use{" "}
                    <strong>India time (IST)</strong>. Open each tab to change dates or folders.
                  </Text>

                  <Textarea
                    value={voiceDraft}
                    onChange={(e) => setVoiceDraft(e.target.value)}
                    placeholder='Example: "Add expense 500 rupees groceries" or "Mark standup done"'
                    size="md"
                    rows={4}
                    bg={voiceComposerBg}
                    borderColor={border}
                    borderRadius="xl"
                    w="100%"
                  />

                  <HStack spacing={3} flexWrap="wrap" justify="flex-start">
                    <Button
                      colorScheme="teal"
                      size="md"
                      onClick={() => submitVoice()}
                      isLoading={voiceLoading}
                      loadingText="Sending"
                    >
                      Send
                    </Button>
                    {voiceSpeaking ? (
                      <Button
                        size="md"
                        variant="outline"
                        colorScheme="red"
                        leftIcon={<Icon as={FiVolumeX} />}
                        onClick={stopSpeech}
                      >
                        Stop speaking
                      </Button>
                    ) : null}
                  </HStack>
                </VStack>
              </Flex>

              {voiceReply ? (
                <Box
                  w="100%"
                  p={4}
                  borderRadius="xl"
                  bg={voiceComposerBg}
                  borderWidth="1px"
                  borderColor={border}
                >
                  <Text fontSize="xs" fontWeight="bold" color="teal.600" mb={1} textTransform="uppercase">
                    Reply
                  </Text>
                  <Text fontSize="sm" whiteSpace="pre-wrap">
                    {voiceReply}
                  </Text>
                </Box>
              ) : null}
            </VStack>
          </Box>
        </Box>

        <Box>
          <HStack spacing={2} mb={3}>
            <Heading size="md">AI studio</Heading>
            <Badge colorScheme="purple" borderRadius="full" px={2}>
              Uses AI Usage Credits
            </Badge>
          </HStack>
          <Text fontSize="sm" color={muted} mb={4}>
            Capture, analyze, and ask — screen, media, audio, video, exams, your
            days, and a traveller board.
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
            {AI_STUDIO.map((f) => (
              <FeatureCard
                key={f.to}
                to={f.to}
                title={f.title}
                icon={f.icon}
                desc={f.desc}
                aiBadge={f.to !== "transfer-ai"}
                accentColor={f.accentColor}
                cardBg={cardBg}
                border={border}
                accent={accent}
              />
            ))}
          </SimpleGrid>
        </Box>

        <Box>
          <Heading size="md" mb={3}>
            Voice-driven tabs
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {VOICE_DOMAINS.map((f) => (
              <FeatureCard
                key={f.id}
                to={f.to}
                title={f.label}
                icon={
                  f.id === "portfolio"
                    ? FaBriefcase
                    : f.id === "projects"
                      ? FaClipboardList
                      : f.id === "daily-chore"
                        ? FaTasks
                        : FaCheckSquare
                }
                desc={f.hint}
                voice
                cardBg={cardBg}
                border={border}
                accent={accent}
              />
            ))}
          </SimpleGrid>
        </Box>

        <Box>
          <Heading size="md" mb={3}>
            Everyday tools
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
            {OTHER_FEATURES.map((f) => (
              <FeatureCard
                key={f.to}
                to={f.to}
                title={f.title}
                icon={f.icon}
                desc={f.desc}
                voice={f.voice}
                cardBg={cardBg}
                border={border}
                accent={accent}
              />
            ))}
          </SimpleGrid>
        </Box>
      </VStack>
    </Box>
  );
};

export default DashboardHome;
